import React, { useState, useRef } from "react";
import * as XLSX from "xlsx";
import { MaitreOeuvre, Org } from "../types/basicTypes";
import { generateID } from "../pages/attestations/helpers/generalHelpers";
import { getFlutterTime } from "../models/DataFunctions";
import createDocApi from "../api/database/createDocApi";
import SecondaryButton from "../buttons/SecondaryButton";
import PrimaryButtonWhite from "../buttons/PrimaryButtonWhite";
import SexySpin from "../assets/SexySpinner";

interface Partner {
  nom: string;
  email: string;
  ville?: string;
  code_postal?: string;
  adresse?: string;
  telephone?: string;
  siret?: string;
}

interface PartnerImportComponentProps {
  onClose: () => void; // Optional prop to handle closing the component
  orgData: Org; // Assuming orgData is passed as a prop
  prefix: string; // Prefix for the database collection
}

const PartnerImportComponent: React.FC<PartnerImportComponentProps> = ({
  onClose,
  orgData,
  prefix,
}) => {
  const [fileData, setFileData] = useState<Partner[]>([]);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string>("");
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Required headers mapping
  const requiredHeaders: Record<keyof Partner, string[]> = {
    nom: [
      "nom",
      "name",
      "nom_entreprise",
      "company_name",
      "company",
      "entreprise",
    ],
    email: ["email", "e-mail", "mail", "email_address", "e_mail"],
    ville: ["ville", "city", "town", "localité"],
    code_postal: ["code_postal", "postal_code", "zip_code", "cp", "zip"],
    adresse: ["adresse", "address", "street", "rue", "street_address"],
    telephone: ["telephone", "phone", "tel", "mobile", "téléphone"],
    siret: ["siret", "siren", "registration_number", "numero_siret"],
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setFileName(file.name);
      processFile(file);
    }
  };

  const onCreatePartner = async (partner: Partner) => {
    let mo: MaitreOeuvre = {
      id: generateID("partner"),
      name: partner.nom,
      city: partner.ville || "",
      postal: partner.code_postal || "",
      address: partner.adresse || "",
      email: partner.email,
      phone: partner.telephone || "",
      siret: partner.siret || "",
      creation_date: getFlutterTime(),
      org_id: orgData.id,
      country: "FR",
      partner_type: "other",
    };
    await createDocApi("partner", mo, prefix);
  };

  const processFile = async (file: File) => {
    setIsLoading(true);
    setValidationErrors([]);

    try {
      const arrayBuffer = await file.arrayBuffer();
      let data: any[];

      if (file.name.endsWith(".csv")) {
        // Handle CSV
        const text = new TextDecoder().decode(arrayBuffer);
        const lines = text.split("\n").filter((line) => line.trim() !== "");
        const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());

        data = lines
          .slice(1)
          .map((line) => {
            const values = line
              .split(",")
              .map((v) => v.trim().replace(/^"|"$/g, ""));
            const row: any = {};
            headers.forEach((header, index) => {
              row[header] = values[index] || "";
            });
            return row;
          })
          .filter((row) => Object.values(row).some((val) => val !== ""));
      } else {
        // Handle Excel
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        data = jsonData.map((row) => {
          const normalizedRow: any = {};
          Object.keys(row as object).forEach((key) => {
            normalizedRow[key.toLowerCase().trim()] = (row as any)[key];
          });
          return normalizedRow;
        });
      }

      // Validate headers and map data
      const mappedData = validateAndMapHeaders(data);

      if (mappedData.length === 0) {
        setValidationErrors(["Aucune donnée valide trouvée dans le fichier"]);
        return;
      }

      setFileData(mappedData);
      setSelectedRows(new Set(mappedData.map((_, index) => index)));
    } catch (error) {
      console.error("Error processing file:", error);
      setValidationErrors(["Erreur lors du traitement du fichier"]);
    } finally {
      setIsLoading(false);
    }
  };

  const validateAndMapHeaders = (data: any[]): Partner[] => {
    const errors: string[] = [];
    const headerMapping: Record<string, keyof Partner> = {};

    if (data.length === 0) {
      errors.push("Le fichier est vide");
      setValidationErrors(errors);
      return [];
    }

    const fileHeaders = Object.keys(data[0]);

    // Map file headers to required headers
    Object.keys(requiredHeaders).forEach((requiredHeader) => {
      const possibleHeaders = requiredHeaders[requiredHeader as keyof Partner];
      const foundHeader = fileHeaders.find((fileHeader) =>
        possibleHeaders.some((possible) =>
          fileHeader.toLowerCase().includes(possible.toLowerCase())
        )
      );
      if (foundHeader) {
        headerMapping[foundHeader] = requiredHeader as keyof Partner;
      }
    });

    // Check if we have required fields
    const hasNom = Object.values(headerMapping).includes("nom");
    const hasEmail = Object.values(headerMapping).includes("email");

    if (!hasNom) {
      errors.push('Colonne "nom" manquante ou non reconnue');
    }
    if (!hasEmail) {
      errors.push('Colonne "email" manquante ou non reconnue');
    }

    if (errors.length > 0) {
      setValidationErrors(errors);
      return [];
    }

    // Transform data using mapping
    const mappedData = data
      .map((row, index) => {
        const mappedRow: Partial<Partner> = {};
        Object.keys(headerMapping).forEach((fileHeader) => {
          const requiredHeader = headerMapping[fileHeader];
          mappedRow[requiredHeader] = String(row[fileHeader] || "").trim();
        });
        return mappedRow;
      })
      .filter((row): row is Partner => {
        return !!(row.nom && row.email && row.email.includes("@"));
      });

    if (mappedData.length === 0) {
      errors.push("Aucune ligne valide trouvée (nom et email requis)");
      setValidationErrors(errors);
    }

    return mappedData;
  };

  const toggleRowSelection = (index: number) => {
    const newSelection = new Set(selectedRows);
    if (newSelection.has(index)) {
      newSelection.delete(index);
    } else {
      newSelection.add(index);
    }
    setSelectedRows(newSelection);
  };

  const selectAll = () => {
    if (selectedRows.size === fileData.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(fileData.map((_, index) => index)));
    }
  };

  const importPartners = async () => {
    if (selectedRows.size === 0) {
      alert("Veuillez sélectionner au moins un partenaire à importer");
      return;
    }

    setIsImporting(true);
    try {
      const selectedData = fileData.filter((_, index) =>
        selectedRows.has(index)
      );

      for (const partner of selectedData) {
        await onCreatePartner(partner);
      }

      resetComponent();
    } catch (error) {
      console.error("Import error:", error);
      alert("Erreur lors de l'importation des partenaires");
    } finally {
      setIsImporting(false);
      onClose();
    }
  };

  const resetComponent = () => {
    setFileData([]);
    setSelectedRows(new Set());
    setFileName("");
    setValidationErrors([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const renderTable = () => {
    if (fileData.length === 0) return null;

    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">
            Données extraites ({fileData.length} lignes)
          </h3>
          <button
            onClick={selectAll}
            className="bg-gray-100 hover:bg-gray-200 border border-gray-300 px-4 py-2 rounded-md text-sm font-medium transition-colors"
            type="button"
          >
            {selectedRows.size === fileData.length
              ? "Tout désélectionner"
              : "Tout sélectionner"}
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-[800px]">
            <thead>
              <tr className="bg-gray-50">
                <th className="p-3 text-left border-b border-gray-200 font-semibold text-gray-700">
                  Sélection
                </th>
                <th className="p-3 text-left border-b border-gray-200 font-semibold text-gray-700">
                  Nom *
                </th>
                <th className="p-3 text-left border-b border-gray-200 font-semibold text-gray-700">
                  Email *
                </th>
                <th className="p-3 text-left border-b border-gray-200 font-semibold text-gray-700">
                  Ville
                </th>
                <th className="p-3 text-left border-b border-gray-200 font-semibold text-gray-700">
                  Code Postal
                </th>
                <th className="p-3 text-left border-b border-gray-200 font-semibold text-gray-700">
                  Adresse
                </th>
                <th className="p-3 text-left border-b border-gray-200 font-semibold text-gray-700">
                  Téléphone
                </th>
                <th className="p-3 text-left border-b border-gray-200 font-semibold text-gray-700">
                  SIRET
                </th>
              </tr>
            </thead>
            <tbody>
              {fileData.map((row, index) => (
                <tr
                  key={index}
                  className={`hover:bg-gray-50 transition-colors ${
                    selectedRows.has(index) ? "bg-blue-50" : ""
                  }`}
                >
                  <td className="p-3 border-b border-gray-200">
                    <input
                      type="checkbox"
                      checked={selectedRows.has(index)}
                      onChange={() => toggleRowSelection(index)}
                      className="w-4 h-4 cursor-pointer accent-blue-500"
                    />
                  </td>
                  <td className="p-3 border-b border-gray-200">{row.nom}</td>
                  <td className="p-3 border-b border-gray-200">{row.email}</td>
                  <td className="p-3 border-b border-gray-200">
                    {row.ville || ""}
                  </td>
                  <td className="p-3 border-b border-gray-200">
                    {row.code_postal || ""}
                  </td>
                  <td className="p-3 border-b border-gray-200">
                    {row.adresse || ""}
                  </td>
                  <td className="p-3 border-b border-gray-200">
                    {row.telephone || ""}
                  </td>
                  <td className="p-3 border-b border-gray-200">
                    {row.siret || ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl ml-2 p-6 font-sans">
      {isImporting && (
        <div className="fixed top-0 left-0 w-full h-full bg-white bg-opacity-50 z-50 flex items-center justify-center">
          <SexySpin></SexySpin>
        </div>
      )}
      <PrimaryButtonWhite onClick={onClose}>Annuler</PrimaryButtonWhite>
      <h1 className="text-2xl font-bold mb-4 mt-8  text-gray-800">
        Importer des Partenaires
      </h1>

      <p className="text-base text-gray-600 mb-3 ">
        Sélectionnez un fichier Excel (.xlsx) ou CSV contenant les données des
        partenaires
      </p>

      <p className="text-sm text-gray-500 mb-6  italic">
        Colonnes requises: <strong>nom*</strong>, <strong>email*</strong>,
        ville, code_postal, adresse, telephone, siret
      </p>

      <div className="mb-6 ">
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleFileSelect}
          className="hidden"
          id="file-input"
        />
        <label htmlFor="file-input">
          <button
            type="button"
            className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg text-base font-medium transition-colors cursor-pointer"
            disabled={isLoading}
            onClick={() => fileInputRef.current?.click()}
          >
            {isLoading ? "Traitement..." : "Sélectionner un fichier"}
          </button>
        </label>
        {fileName && (
          <div className="mt-3 text-sm text-gray-600">
            Fichier sélectionné: {fileName}
          </div>
        )}
      </div>

      {validationErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          {validationErrors.map((error, index) => (
            <div key={index} className="text-red-600 mb-1">
              ❌ {error}
            </div>
          ))}
        </div>
      )}

      {isLoading && (
        <div className=" py-6 text-gray-600">
          📄 Traitement du fichier en cours...
        </div>
      )}

      {renderTable()}

      {fileData.length > 0 && (
        <button
          className="bg-green-500 hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-8 py-4 rounded-lg text-base font-medium transition-colors block mx-auto mt-6"
          onClick={importPartners}
          disabled={selectedRows.size === 0 || isImporting}
        >
          {isImporting
            ? "Import en cours..."
            : `Importer les partenaires (${selectedRows.size})`}
        </button>
      )}
    </div>
  );
};

export default PartnerImportComponent;
